import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { getMe, postAccess } from "@/api/auth";
import { ApiError } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import accessRadarWordmark from "@/assets/access-radar-wordmark.svg";
import AccessLogoBrand from "@/components/access/AccessLogoBrand";
import AccessMessage, { type AccessMessageKind } from "@/components/access/AccessMessage";
import { useMe } from "@/hooks/useAuth";
import { hasUnsubscribeIntent, UNSUBSCRIBE_LOGIN_NOTICE } from "@/lib/unsubscribe";

function accessMessageKind(error: unknown): AccessMessageKind {
  if (!(error instanceof ApiError)) return "denied";
  if (error.message === "too_many_attempts") return "expired";
  return "denied";
}

export default function Access() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const me = useMe();
  const [email, setEmail] = useState("");
  const year = new Date().getFullYear();

  const from =
    (location.state as { from?: string } | null)?.from &&
    (location.state as { from?: string }).from !== "/access"
      ? (location.state as { from: string }).from
      : "/";
  const showUnsubscribeNotice = hasUnsubscribeIntent(from);

  useEffect(() => {
    if (me.isSuccess) {
      navigate(from, { replace: true });
    }
  }, [me.isSuccess, navigate, from]);

  const login = useMutation({
    mutationFn: (value: string) => postAccess(value.trim().toLowerCase()),
    onSuccess: async () => {
      try {
        await queryClient.fetchQuery({
          queryKey: queryKeys.auth.me(),
          queryFn: getMe,
          staleTime: 0,
        });
        navigate(from, { replace: true });
      } catch {
        await queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() });
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || login.isPending) return;
    login.mutate(email);
  };

  if (me.isLoading) {
    return (
      <div className="access-page flex h-full items-center justify-center" role="status">
        <p className="text-base font-medium text-[#00402a]/70">Comprobando sesión…</p>
      </div>
    );
  }

  return (
    <div className="access-page">
      <div className="access-shell relative mx-auto h-full w-full">
        <header className="access-shell__brand">
          <AccessLogoBrand />
        </header>

        <main className="access-main">
          <div className="access-main__copy">
            <h1 className="access-title">
              <img
                src={accessRadarWordmark}
                alt="Radar by Iberdrola"
                className="access-title__logo"
                width={536}
                height={262}
                decoding="async"
              />
            </h1>

            <form className="access-form" onSubmit={handleSubmit} noValidate>
              <label htmlFor="access-email" className="sr-only">
                Correo electrónico
              </label>
              <div className="access-form__field">
                <input
                  id="access-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@iberdrola.com"
                  disabled={login.isPending}
                  className="access-form__input"
                />
                <button
                  type="submit"
                  className="access-form__submit"
                  aria-label="Entrar"
                  disabled={login.isPending || !email.trim()}
                >
                  <svg
                    className="access-form__submit-icon"
                    width={56}
                    height={56}
                    viewBox="0 0 56 56"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle cx="28" cy="28" r="28" fill="#FFA61C" />
                    <g className="access-form__submit-arrow">
                      <path
                        d="M27.8906 17.78L37.8976 28L27.8906 38.22"
                        stroke="#003F28"
                        strokeWidth={2}
                      />
                      <path d="M38.22 28.0278L17.78 28.0278" stroke="#003F28" strokeWidth={2} />
                    </g>
                  </svg>
                </button>
              </div>
            </form>

            {showUnsubscribeNotice ? (
              <p className="mt-3 text-sm font-medium text-[#00402a]/80" role="status">
                {UNSUBSCRIBE_LOGIN_NOTICE}
              </p>
            ) : null}

            {login.isError && <AccessMessage kind={accessMessageKind(login.error)} />}
            {login.isPending && <AccessMessage kind="success" />}
          </div>
        </main>

        <footer className="access-footer">
          <p className="access-footer__year">®Iberdrola {year}</p>
        </footer>
      </div>
    </div>
  );
}
